import { handleOrganizationAccessError } from "@/lib/organization-access-errors";
import { notifyError } from "@/lib/notify-error";
import { useEffect, useId, useRef, useState } from "react";
import type { RefObject } from "react";
import { Copy, Flag, Group, Trash2, Ungroup } from "lucide-react";
import { toast } from "sonner";

import type { Doc, Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ViewportSelectionPanel } from "@/components/ui/viewport-selection-panel";
import { resolveTagLabel } from "@/features/field-pilot/label-utils";
import { StatusOptionLabel } from "@/features/field-pilot/status-dot";
import { getUserFacingErrorMessage } from "@/lib/user-facing-errors";
import {
  canGroupSelectedMeasurements,
  selectionLabelGroupState,
} from "@/viewport-tools/measurement/overlay/measurement-label-groups";
import {
  applyMeasurementLeaderVisibility,
  shouldShowMeasurementLeader,
} from "@/viewport-tools/measurement/overlay/measurement-overlay-helpers";
import type { MeasurementOverlayRecord } from "@/viewport-tools/measurement/overlay/measurement-overlay-types";
import { useCanEdit } from "@/lib/capabilities";

export const NEW_SUB_TASK_OPTION = "__new_sub_task__";
export const NONE_SUB_TASK_OPTION = "__none__";

const compactSelectTriggerClassName = "h-7 min-w-0 px-2 text-xs";
const MIN_STROKE_WIDTH = 0.5;
const MAX_STROKE_WIDTH = 20;
const STROKE_WIDTH_RANGE_ERROR = `Stroke width must be between ${MIN_STROKE_WIDTH} and ${MAX_STROKE_WIDTH}.`;

function isValidStrokeWidth(width: number) {
  return Number.isFinite(width) && width >= MIN_STROKE_WIDTH && width <= MAX_STROKE_WIDTH;
}

function milestoneActionLabel(
  item: Doc<"fieldPilotItems">,
  selectedCount: number
): string {
  return item.subTaskId || selectedCount > 1
    ? "Edit milestones"
    : "Set milestones (creates a sub-task)";
}

export function FieldSelectedItemCard({
  item,
  selectedCount,
  itemLabel,
  selectedMeasurements,
  tags,
  statuses,
  subTasks,
  subTaskSelectValue,
  subTaskPlaceholder,
  tagSelectValue,
  tagPlaceholder,
  statusSelectValue,
  statusPlaceholder,
  spansMultipleTags,
  onApplyTag,
  onApplyStatus,
  onAssignToSubTask,
  onClearSubTask,
  onApplyMeasurementLeaders,
  onApplyStrokeWidth,
  onPreviewStrokeWidth,
  onGroupLabels,
  onUngroupLabels,
  onGroupAllLabelsInScope,
  onUngroupAllLabelsInScope,
  onDuplicate,
  onGroupIntoSubTask,
  onOpenMilestones,
  onDelete,
  onClose,
}: {
  item: Doc<"fieldPilotItems"> | null;
  selectedCount: number;
  itemLabel: string;
  selectedMeasurements: MeasurementOverlayRecord[];
  tags: Doc<"fieldPilotTags">[];
  statuses: Doc<"fieldPilotStatuses">[];
  subTasks: { subTaskId: Id<"fieldPilotSubTasks">; name: string }[];
  /** Controlled Select value: sub-task id, `__none__`, or "" when mixed. */
  subTaskSelectValue: string;
  subTaskPlaceholder: string;
  /** Controlled Select value: tag id, or "" when the selection has mixed tags. */
  tagSelectValue: string;
  tagPlaceholder: string;
  /** Controlled Select value: status id, or "" when the selection has mixed statuses. */
  statusSelectValue: string;
  statusPlaceholder: string;
  spansMultipleTags: boolean;
  onApplyTag: (tagId: Id<"fieldPilotTags">) => Promise<void>;
  onApplyStatus: (statusId: Id<"fieldPilotStatuses">) => Promise<void>;
  onAssignToSubTask: (subTaskId: Id<"fieldPilotSubTasks">) => void;
  onClearSubTask: () => void;
  onApplyMeasurementLeaders?: (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    leaderVisible: boolean
  ) => Promise<boolean>;
  onApplyStrokeWidth?: (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    strokeWidth: number
  ) => Promise<boolean>;
  onPreviewStrokeWidth?: (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    strokeWidth: number | null
  ) => ((saved: boolean) => void) | void;
  onGroupLabels?: (measurementIds: Id<"takeoffPilotMeasurements">[]) => Promise<boolean>;
  onUngroupLabels?: (measurementIds: Id<"takeoffPilotMeasurements">[]) => Promise<boolean>;
  onGroupAllLabelsInScope?: () => Promise<boolean>;
  onUngroupAllLabelsInScope?: () => Promise<boolean>;
  onDuplicate: () => void;
  onGroupIntoSubTask: () => void;
  onOpenMilestones: () => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  // The card is an item editor (tag, status, sub-task, labels, duplicate, delete); reviewers read
  // items on the canvas and in the progress table instead.
  const canEdit = useCanEdit();
  // Read by the stroke-width control's unmount cleanup, which runs after this render commits.
  const canEditRef = useRef(canEdit);
  canEditRef.current = canEdit;
  const [leaderVisibleDraft, setLeaderVisibleDraft] = useState<boolean | "indeterminate">(false);
  const [isSavingLeader, setIsSavingLeader] = useState(false);

  useEffect(() => {
    const selectedLeadersCount = selectedMeasurements.filter((measurement) =>
      shouldShowMeasurementLeader(measurement.annotation)
    ).length;
    const hasMixedLeaders =
      selectedLeadersCount > 0 && selectedLeadersCount < selectedMeasurements.length;
    const allLeadersVisible = selectedLeadersCount === selectedMeasurements.length;
    setLeaderVisibleDraft(hasMixedLeaders ? "indeterminate" : allLeadersVisible);
  }, [selectedMeasurements]);

  if (!item || !canEdit) {
    return null;
  }

  const canApplyLeaderUpdates = !!onApplyMeasurementLeaders;
  const measurementIds = selectedMeasurements.map((measurement) => measurement._id);
  const groupEligibility = canGroupSelectedMeasurements(selectedMeasurements);
  const labelGroupState = selectionLabelGroupState(selectedMeasurements);
  const canGroupSelection = !!onGroupLabels && groupEligibility.ok;
  const canUngroupSelection = !!onUngroupLabels && labelGroupState.anyGrouped;
  const selectedSubTaskKeySet = new Set(
    selectedMeasurements.map((m) => m.labelGroupScopeKey?.split(":")[1] ?? "")
  );
  const spansMultipleSubTasks = selectedSubTaskKeySet.size > 1;
  const canOpenMilestones =
    selectedCount === 1 ||
    (subTaskSelectValue !== "" && subTaskSelectValue !== NONE_SUB_TASK_OPTION);
  const selectedStatusName =
    statuses.find((status) => String(status._id) === statusSelectValue)?.name ?? statusPlaceholder;

  const handleTagChange = (nextTagId: string) => {
    // Tag applies to every selected item (the card edits the whole selection).
    void onApplyTag(nextTagId as Id<"fieldPilotTags">);
  };

  const handleStatusChange = (nextStatusId: string) => {
    // Status applies to every selected item (the card edits the whole selection).
    void onApplyStatus(nextStatusId as Id<"fieldPilotStatuses">);
  };

  const handleSubTaskChange = (value: string) => {
    // Ungrouping has no single-tag constraint — allow None across multi-tag selections.
    if (value === NONE_SUB_TASK_OPTION) {
      onClearSubTask();
      return;
    }
    if (spansMultipleTags) {
      return;
    }
    if (value === NEW_SUB_TASK_OPTION) {
      onGroupIntoSubTask();
      return;
    }
    onAssignToSubTask(value as Id<"fieldPilotSubTasks">);
  };

  const handleLeaderChange = async (checked: boolean) => {
    if (!canApplyLeaderUpdates || measurementIds.length === 0) {
      return;
    }
    const previousLeaderVisible = leaderVisibleDraft;
    setLeaderVisibleDraft(checked);
    setIsSavingLeader(true);
    try {
      await applyMeasurementLeaderVisibility(measurementIds, checked, {
        onApplyMeasurementLeaders,
      });
      toast.success(
        measurementIds.length > 1
          ? `${measurementIds.length} items updated`
          : "Item updated"
      );
    } catch (error) {
      setLeaderVisibleDraft(previousLeaderVisible);
      notifyError(error, getUserFacingErrorMessage(error, "Failed to update measurement leaders"));
    } finally {
      setIsSavingLeader(false);
    }
  };

  return (
    <ViewportSelectionPanel
      title="Field Item"
      subtitle={selectedCount > 1 ? `${selectedCount} items selected` : itemLabel}
      onClose={onClose}
      closeLabel="Close item editor"
      className="text-xs"
      actions={
        <>
          {canOpenMilestones ? (
            <IconButton
              label={milestoneActionLabel(item, selectedCount)}
              icon={Flag}
              variant="outline"
              onClick={onOpenMilestones}
            />
          ) : null}
          {onGroupLabels && canGroupSelection ? (
            <IconButton
              label="Group labels"
              icon={Group}
              variant="outline"
              disabled={isSavingLeader}
              onClick={() => {
                void (async () => {
                  const ok = await onGroupLabels(measurementIds);
                  if (ok) toast.success("Labels grouped");
                })();
              }}
            />
          ) : null}
          {onUngroupLabels && canUngroupSelection ? (
            <IconButton
              label="Ungroup labels"
              icon={Ungroup}
              variant="outline"
              disabled={isSavingLeader}
              onClick={() => {
                void (async () => {
                  const ok = await onUngroupLabels(measurementIds);
                  if (ok) toast.success("Labels ungrouped");
                })();
              }}
            />
          ) : null}
          <IconButton label="Duplicate" icon={Copy} variant="outline" onClick={onDuplicate} />
          <IconButton
            label="Delete field item"
            tooltip="Delete"
            icon={Trash2}
            variant="destructive"
            onClick={onDelete}
          />
        </>
      }
    >
      {selectedCount === 1 && item.geometry.type === "point" ? (
        <p className="text-[10px] text-muted-foreground">
          Source: {item.sourceDocumentDetectionId ? "Detected tag" : "Manual point"}
        </p>
      ) : null}
      <div className="grid grid-cols-2 gap-2">
        <div className="min-w-0 space-y-1">
          <Label className="text-[11px]">{selectedCount > 1 ? "Tag (all)" : "Tag"}</Label>
          <Select value={tagSelectValue} onValueChange={handleTagChange}>
            <SelectTrigger className={compactSelectTriggerClassName}>
              <SelectValue placeholder={tagPlaceholder} />
            </SelectTrigger>
            <SelectContent className="z-[160] text-xs">
              {tags.map((tag) => (
                <SelectItem key={tag._id} value={tag._id} className="text-xs">
                  {resolveTagLabel(tag)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-0 space-y-1">
          <Label className="text-[11px]">{selectedCount > 1 ? "Status (all)" : "Status"}</Label>
          <Select value={statusSelectValue} onValueChange={handleStatusChange}>
            <SelectTrigger
              className={compactSelectTriggerClassName}
              aria-label={selectedCount > 1 ? "Status for selected items" : "Status"}
              title={selectedStatusName}
            >
              <SelectValue placeholder={statusPlaceholder} />
            </SelectTrigger>
            <SelectContent className="z-[160] text-xs">
              {statuses.map((status) => (
                <SelectItem key={status._id} value={status._id} className="text-xs">
                  <StatusOptionLabel
                    className="gap-1.5"
                    colorHex={status.colorHex}
                    name={status.name}
                  />
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1">
        <Label className="text-[11px]">Sub-task</Label>
        <Select value={subTaskSelectValue} onValueChange={handleSubTaskChange}>
          <SelectTrigger className={compactSelectTriggerClassName}>
            <SelectValue placeholder={subTaskPlaceholder} />
          </SelectTrigger>
          <SelectContent className="z-[160] text-xs">
            <SelectItem value={NONE_SUB_TASK_OPTION} className="text-xs">
              None
            </SelectItem>
            {subTasks.map((subTask) => (
              <SelectItem
                key={String(subTask.subTaskId)}
                value={String(subTask.subTaskId)}
                disabled={spansMultipleTags}
                className="text-xs"
              >
                {subTask.name}
              </SelectItem>
            ))}
            <SelectItem value={NEW_SUB_TASK_OPTION} disabled={spansMultipleTags} className="text-xs">
              + New sub-task…
            </SelectItem>
          </SelectContent>
        </Select>
        {spansMultipleTags ? (
          <p className="text-[10px] text-muted-foreground">
            Choose None to ungroup across tags, or select items from a single tag to assign.
          </p>
        ) : null}
      </div>

      {measurementIds.length > 0 ? (
        <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
          <label className="flex min-h-7 items-center gap-2 text-[11px] text-muted-foreground">
            <Checkbox
              aria-label={
                selectedCount > 1
                  ? "Show leader for selected measurements"
                  : "Show measurement leader"
              }
              checked={leaderVisibleDraft}
              onCheckedChange={(checked) => void handleLeaderChange(checked === true)}
              disabled={!canApplyLeaderUpdates || isSavingLeader}
            />
            Leader
          </label>
          {onApplyStrokeWidth ? (
            <FieldStrokeWidthControl
              key={measurementIds.join(",")}
              selectedMeasurements={selectedMeasurements}
              selectedCount={selectedCount}
              onApplyStrokeWidth={onApplyStrokeWidth}
              onPreviewStrokeWidth={onPreviewStrokeWidth}
              canEditRef={canEditRef}
            />
          ) : null}
        </div>
      ) : null}

      {measurementIds.length > 0 &&
      (onGroupAllLabelsInScope || onUngroupAllLabelsInScope) ? (
        <div className="flex flex-wrap gap-1">
          {onGroupAllLabelsInScope ? (
            <Button
              type="button"
              size="xs"
              variant="outline"
              disabled={spansMultipleTags || spansMultipleSubTasks || isSavingLeader}
              onClick={() => {
                void (async () => {
                  const ok = await onGroupAllLabelsInScope();
                  if (ok) toast.success("Grouped all labels in scope");
                })();
              }}
            >
              Group all in scope
            </Button>
          ) : null}
          {onUngroupAllLabelsInScope ? (
            <Button
              type="button"
              size="xs"
              variant="outline"
              disabled={spansMultipleTags || spansMultipleSubTasks || isSavingLeader}
              onClick={() => {
                void (async () => {
                  const ok = await onUngroupAllLabelsInScope();
                  if (ok) toast.success("Ungrouped all labels in scope");
                })();
              }}
            >
              Ungroup all in scope
            </Button>
          ) : null}
        </div>
      ) : null}
    </ViewportSelectionPanel>
  );
}

function FieldStrokeWidthControl({
  selectedMeasurements,
  selectedCount,
  onApplyStrokeWidth,
  onPreviewStrokeWidth,
  canEditRef,
}: {
  selectedMeasurements: MeasurementOverlayRecord[];
  selectedCount: number;
  onApplyStrokeWidth: (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    strokeWidth: number
  ) => Promise<boolean>;
  onPreviewStrokeWidth?: (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    strokeWidth: number | null
  ) => ((saved: boolean) => void) | void;
  canEditRef: RefObject<boolean>;
}) {
  const measurementIds = selectedMeasurements.map((measurement) => measurement._id);
  const widths = selectedMeasurements.map((measurement) => measurement.annotation?.strokeWidth ?? 2);
  const sharedWidth = widths.every((width) => width === widths[0]) ? String(widths[0]) : "";
  const [draft, setDraft] = useState(sharedWidth);
  const [error, setError] = useState<string | null>(null);
  const inputId = useId();
  const savedWidthRef = useRef(sharedWidth);
  const pendingWidthRef = useRef<number | null>(null);
  const finishPreviewRef = useRef<((saved: boolean) => void) | void>(undefined);
  const queuedCommitRef = useRef<{
    width: number;
    editVersion: number;
    finishPreview: ((saved: boolean) => void) | void;
  } | null>(null);
  const editVersionRef = useRef(0);
  const savingRef = useRef(false);
  const mountedRef = useRef(true);
  const flushRef = useRef<() => Promise<void>>(async () => {});
  const saveRef = useRef<(
    width: number, editVersion: number, finishPreview: ((saved: boolean) => void) | void
  ) => Promise<void>>(async () => {});
  const clearPreviewRef = useRef(() => {});
  clearPreviewRef.current = () => onPreviewStrokeWidth?.(measurementIds, null);

  useEffect(() => {
    if (pendingWidthRef.current !== null || savingRef.current) return;
    savedWidthRef.current = sharedWidth;
    setDraft(sharedWidth);
    setError(null);
  }, [sharedWidth]);

  const save = async (
    width: number, editVersion: number, finishPreview: ((saved: boolean) => void) | void
  ) => {
    if (savingRef.current) {
      queuedCommitRef.current = { width, editVersion, finishPreview };
      return;
    }
    if (String(width) === savedWidthRef.current) {
      finishPreview?.(true);
      return;
    }
    savingRef.current = true;
    try {
      const saved = await onApplyStrokeWidth(measurementIds, width);
      if (!saved) throw new Error("Failed to save stroke width. Try again.");
      savedWidthRef.current = String(width);
      finishPreview?.(true);
      // The overlay retains the preview until the query reflects the saved value.
    } catch (saveError) {
      finishPreview?.(false);
      if (handleOrganizationAccessError(saveError)) {
        clearPreviewRef.current();
        return;
      }
      const message = getUserFacingErrorMessage(saveError, "Failed to save stroke width. Try again.");
      if (!mountedRef.current) toast.error(message);
      else if (editVersionRef.current === editVersion) {
        setError(message);
        clearPreviewRef.current();
      }
    } finally {
      savingRef.current = false;
      const queued = queuedCommitRef.current;
      queuedCommitRef.current = null;
      if (queued) void saveRef.current(queued.width, queued.editVersion, queued.finishPreview);
    }
  };
  saveRef.current = save;

  const flush = async () => {
    const width = pendingWidthRef.current;
    if (width === null) return;
    pendingWidthRef.current = null;
    await save(width, editVersionRef.current, finishPreviewRef.current);
  };
  flushRef.current = flush;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Losing edit access discards a pending edit, since saving it would be a rejected write.
      if (!canEditRef.current) {
        pendingWidthRef.current = null;
        queuedCommitRef.current = null;
        clearPreviewRef.current();
        return;
      }
      // Switching selection or closing the card must not discard a pending edit.
      void flushRef.current();
    };
  }, []);

  const updateDraft = (value: string) => {
    setDraft(value);
    setError(null);
    editVersionRef.current += 1;
    pendingWidthRef.current = null;
    const width = Number(value);
    if (!isValidStrokeWidth(width) ||
      (!savingRef.current && String(width) === savedWidthRef.current)) {
      clearPreviewRef.current();
      return;
    }
    finishPreviewRef.current = onPreviewStrokeWidth?.(measurementIds, width);
    pendingWidthRef.current = width;
  };

  const finishEditing = () => {
    if (!savingRef.current && draft === savedWidthRef.current && pendingWidthRef.current === null) return;
    const width = Number(draft);
    if (!isValidStrokeWidth(width)) {
      setError(STROKE_WIDTH_RANGE_ERROR);
      return;
    }
    if (savingRef.current || String(width) !== savedWidthRef.current) {
      pendingWidthRef.current = width;
      setError(null);
    }
    void flush();
  };

  return (
    <div className="min-w-0 space-y-1">
      <div className="flex items-center gap-1.5">
        <Label htmlFor={inputId} className="text-[11px] text-muted-foreground">
          Width
        </Label>
        <Input
          id={inputId}
          aria-label={selectedCount > 1 ? "Stroke width (all)" : "Stroke width"}
          type="number"
          min={MIN_STROKE_WIDTH}
          max={MAX_STROKE_WIDTH}
          step={0.5}
          value={draft}
          placeholder="Mixed"
          onChange={(event) => updateDraft(event.target.value)}
          onBlur={finishEditing}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              finishEditing();
            }
          }}
          aria-invalid={!!error}
          aria-describedby={error ? `${inputId}-error` : undefined}
          className="h-7 w-16 px-1.5 py-0 text-xs md:text-xs"
        />
      </div>
      {error ? (
        <p id={`${inputId}-error`} role="alert" className="max-w-36 text-[11px] text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
